import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, FileText, X, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import logger from '@/utils/logger';

interface PaymentProofUploaderProps {
  onGetUploadParameters: () => Promise<{
    method: "PUT";
    url: string;
  }>;
  onComplete?: (result: { uploadURL: string; filename: string }) => void;
  buttonClassName?: string;
  children: React.ReactNode;
}

export function PaymentProofUploader({
  onGetUploadParameters,
  onComplete,
  buttonClassName,
  children,
}: PaymentProofUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid File Type",
          description: "Please upload a PDF, JPEG, or PNG file.",
          variant: "destructive",
        });
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10485760) {
        toast({
          title: "File Too Large",
          description: "File size must be less than 10MB.",
          variant: "destructive",
        });
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      logger.log('No file selected');
      return;
    }

    logger.log('Starting upload for:', selectedFile.name);
    setUploading(true);
    setUploadProgress(0);

    try {
      // Get upload parameters
      logger.log('Requesting upload parameters...');
      const params = await onGetUploadParameters();
      logger.log('Received upload parameters:', params);
      
      const { url } = params;
      if (!url) {
        throw new Error('No upload URL received');
      }

      // Upload file
      const xhr = new XMLHttpRequest();
      
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          setUploadProgress(progress);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 204) {
          // Generate a unique filename for storage reference
          const timestamp = Date.now();
          const fileExtension = selectedFile.name.split('.').pop() || 'pdf';
          const uniqueFilename = `payment-proof-${timestamp}.${fileExtension}`;
          
          onComplete?.({
            uploadURL: `/objects/uploads/${uniqueFilename}`,
            filename: selectedFile.name,
          });
          setSelectedFile(null);
          setUploadProgress(0);
          toast({
            title: "Upload Successful",
            description: "Payment proof uploaded successfully. You can now send it for approval.",
          });
        } else {
          logger.error('Upload failed with status:', xhr.status);
          toast({
            title: "Upload Failed",
            description: `Upload failed with status: ${xhr.status}`,
            variant: "destructive",
          });
        }
        setUploading(false);
      };

      xhr.onerror = () => {
        toast({
          title: "Upload Failed",
          description: "Network error during upload. Please try again.",
          variant: "destructive",
        });
        setUploading(false);
      };

      // For development, simulate upload
      if (url.includes('mock-bucket')) {
        logger.log('Starting mock upload simulation');
        // Simulate upload progress
        let progress = 0;
        const interval = setInterval(() => {
          progress += 20;
          setUploadProgress(progress);
          if (progress >= 100) {
            clearInterval(interval);
            setTimeout(() => {
              // Complete the upload directly
              const timestamp = Date.now();
              const fileExtension = selectedFile.name.split('.').pop() || 'pdf';
              const uniqueFilename = `payment-proof-${timestamp}.${fileExtension}`;
              
              onComplete?.({
                uploadURL: `/objects/uploads/${uniqueFilename}`,
                filename: selectedFile.name,
              });
              
              setSelectedFile(null);
              setUploadProgress(0);
              setUploading(false);
              
              toast({
                title: "Upload Successful",
                description: "Payment proof uploaded successfully. You can now send it for approval.",
              });
            }, 300);
          }
        }, 100);
        return;
      }
      
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', selectedFile.type);
      xhr.send(selectedFile);

    } catch (error) {
      toast({
        title: "Upload Failed",
        description: "Failed to get upload URL. Please try again.",
        variant: "destructive",
      });
      setUploading(false);
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {/* File Selection Button */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button 
          onClick={() => fileInputRef.current?.click()}
          className={buttonClassName}
          disabled={uploading}
        >
          {children}
        </Button>
      </div>

      {/* Selected File Display */}
      {selectedFile && (
        <Card className="border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-emerald-600" />
                <div>
                  <p className="font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearSelection}
                  disabled={uploading}
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={handleUpload}
                  disabled={uploading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Progress Bar */}
            {uploading && (
              <div className="mt-3">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Uploading...</span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}